import { useCallback, useState } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import {
  Cloud as CloudIcon,
  FileText,
  ChevronRight,
  ArrowRight,
  Check,
  Loader2,
  LogIn,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCloud } from '@/hooks/useCloud';
import { fetchCloudDocumentAsFile, updateCloudThumbnail } from '@/lib/cloudApi';
import { renderPdfThumbnail } from '@/lib/pdfThumbnail';
import { getCloudState, getCloudUpsell } from '@/lib/cloudUpsell';
import { formatBytes } from '@/lib/billing';
import CloudThumbnail from '@/components/CloudThumbnail';

/**
 * CloudColumn — right-hand hero panel on the Dashboard.
 *
 *   cloudState === 'available'  → compact list of recent cloud docs + usage bar
 *   cloudState === 'locked'     → upsell card with tier-aware price
 *   cloudState === 'signed-out' → sign-in prompt
 *
 * Co-located with the existing Dashboard drop zone so "your cloud" is a peer
 * of "your device" instead of a tucked-away rail destination.
 */
export default function CloudColumn() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, account } = useAuth();
  const cloudState = getCloudState(user, account);
  const cloudUpsell = getCloudUpsell(account);
  const { documents, usage, loading, error, refresh } = useCloud({ autoLoad: cloudState === 'available' });
  const [opening, setOpening] = useState(null);

  const onOpen = useCallback(async (doc) => {
    setOpening(doc.id);
    try {
      const file = await fetchCloudDocumentAsFile(doc.id);
      if (!doc.hasThumbnail) {
        (async () => {
          const thumb = await renderPdfThumbnail(file).catch(() => null);
          if (thumb) await updateCloudThumbnail(doc.id, thumb).catch(() => {});
        })();
      }
      navigate('/viewer', { state: { newDocument: true, file, cloudDocId: doc.id } });
    } catch (err) {
      console.error('Open cloud doc failed:', err);
    } finally {
      setOpening(null);
    }
  }, [navigate]);

  if (cloudState === 'signed-out') {
    return (
      <div className="rounded-2xl p-6 border border-brand-light bg-gradient-to-br from-brand-light to-white relative overflow-hidden h-full flex flex-col">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-30"
             style={{ background: 'radial-gradient(circle, #A5B4FC 0%, transparent 70%)' }} />
        <div className="relative flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <CloudIcon size={20} className="text-brand-blue" />
            <span className="text-sm font-bold text-gray-900">{t('common:cloud.signed_out_title', 'Sign in to use Scrivar Cloud')}</span>
          </div>
          <p className="text-[13px] text-gray-600 leading-relaxed">
            {t('common:cloud.signed_out_copy', 'Save your PDFs to the cloud and open them on any signed-in device.')}
          </p>
          <div className="flex-1" />
          <div className="mt-5 flex items-center gap-2">
            <button
              onClick={() => navigate('/login?next=/tools')}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-95 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
            >
              <LogIn size={14} />
              {t('common:cloud.sign_in', 'Sign in')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (cloudState === 'locked') {
    // Tier-aware upsell — pricing, copy, and target product come from
    // cloudUpsell so we never hard-code a price string that can drift.
    return (
      <div className="rounded-2xl p-6 border border-brand-light bg-gradient-to-br from-brand-light to-white relative overflow-hidden h-full flex flex-col">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-30"
             style={{ background: 'radial-gradient(circle, #A5B4FC 0%, transparent 70%)' }} />
        <div className="relative flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <CloudIcon size={20} className="text-brand-blue" />
            <span className="text-sm font-bold text-gray-900">
              {cloudUpsell.isAddon
                ? t('common:cloud.upgrade_addon_title', 'Add cloud to your lifetime license')
                : t('common:cloud.never_lose_title', 'Never lose your work')}
            </span>
          </div>
          <p className="text-[13px] text-gray-600 leading-relaxed">
            {cloudUpsell.isAddon
              ? t('common:cloud.addon_copy', 'Keep your lifetime license and bolt on cloud sync plus AI tools for a single monthly add-on.')
              : t('common:cloud.never_lose_copy', 'Save your PDFs to Scrivar Cloud and open them on any signed-in device. Auto-sync, version history, and shareable links.')}
          </p>
          <ul className="mt-3 space-y-1.5">
            <li className="flex items-center gap-2 text-[12px] text-gray-700">
              <Check size={12} className="text-accent-green" />
              {cloudUpsell.isAddon
                ? t('common:cloud.feature_storage_5gb', '5 GB cloud storage')
                : t('common:cloud.feature_sync', 'Sync across devices')}
            </li>
            <li className="flex items-center gap-2 text-[12px] text-gray-700">
              <Check size={12} className="text-accent-green" />
              {t('common:cloud.feature_history', '30-day version history')}
            </li>
            <li className="flex items-center gap-2 text-[12px] text-gray-700">
              <Check size={12} className="text-accent-green" />
              {t('common:cloud.feature_share', 'Share view / edit links')}
            </li>
          </ul>
          <div className="flex-1" />
          {cloudUpsell.canBuy && (
            <div className="mt-5">
              <a
                href="/account/billing?highlight=cloud"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-95 transition-opacity"
                style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
              >
                {cloudUpsell.ctaLong}
                <ArrowRight size={14} />
              </a>
              <div className="text-[11px] text-gray-400 mt-2">
                {cloudUpsell.isAddon
                  ? t('common:cloud.addon_note', 'Add-on for lifetime licenses · cancel anytime')
                  : t('common:cloud.available_any_tier', 'Cancel anytime · Available on any tier')}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // cloudState === 'available'
  const shownDocs = Array.isArray(documents) ? documents.slice(0, 4) : [];
  const usedBytes = usage?.usedBytes || 0;
  const limitBytes = usage?.limitBytes || (account?.capabilities?.cloud_bytes || 0);
  const usedPct = limitBytes > 0 ? Math.min(100, Math.round((usedBytes / limitBytes) * 100)) : 0;

  return (
    <div className="rounded-2xl p-5 border border-brand-light bg-gradient-to-b from-brand-light/40 to-white h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CloudIcon size={18} className="text-brand-blue" />
          <span className="text-sm font-bold text-gray-900">{t('common:cloud.your_cloud', 'Your cloud')}</span>
        </div>
        <button
          onClick={() => navigate('/cloud')}
          className="text-xs font-semibold text-brand-blue hover:underline inline-flex items-center gap-1"
        >
          {t('common:cloud.open_all', 'Open all')}
          <ArrowRight size={12} />
        </button>
      </div>

      {loading && shownDocs.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-xs flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {t('common:cloud.loading_cloud', 'Loading your cloud…')}
        </div>
      ) : error && shownDocs.length === 0 ? (
        <div className="py-6 text-center">
          <div className="text-xs text-gray-500">
            {t('common:cloud.unreachable', "Can't reach your cloud right now — your saved files are safe.")}
          </div>
          <button
            onClick={() => refresh()}
            className="mt-2 text-xs font-semibold text-brand-blue hover:underline"
          >
            {t('common:cloud.retry', 'Retry')}
          </button>
        </div>
      ) : shownDocs.length === 0 ? (
        <div className="py-6 text-center">
          <div className="text-xs text-gray-500">{t('common:cloud.no_cloud_docs', 'No cloud documents yet.')}</div>
          <button
            onClick={() => navigate('/cloud')}
            className="mt-2 text-xs font-semibold text-brand-blue hover:underline"
          >
            {t('common:cloud.upload_first', 'Upload your first PDF →')}
          </button>
        </div>
      ) : (
        <ul className="space-y-1 flex-1">
          {shownDocs.map((doc) => (
            <li key={doc.id}>
              <button
                onClick={() => onOpen(doc)}
                disabled={opening === doc.id}
                className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-white transition-colors text-left group disabled:opacity-60"
              >
                <div className="w-8 h-10 rounded bg-white border border-gray-200 overflow-hidden flex items-center justify-center shrink-0">
                  {opening === doc.id ? (
                    <Loader2 size={14} className="text-brand-blue animate-spin" />
                  ) : (
                    <CloudThumbnail
                      docId={doc.id}
                      hasThumbnail={doc.hasThumbnail}
                      alt=""
                      className="w-full h-full object-cover"
                      placeholder={<FileText size={14} className="text-accent-coral" />}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{doc.filename}</div>
                  <div className="text-[11px] text-gray-500">
                    {formatBytes(doc.fileSizeBytes)}
                    {doc.pageCount > 0 && ` · ${doc.pageCount} ${doc.pageCount === 1 ? 'page' : 'pages'}`}
                  </div>
                </div>
                <ChevronRight size={14} className="text-gray-300 group-hover:text-brand-blue" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!error && limitBytes > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex justify-between text-[11px] text-gray-500 mb-1">
            <span>{formatBytes(usedBytes)} used</span><span>{formatBytes(limitBytes)}</span>
          </div>
          <div className="h-1.5 rounded bg-gray-100 overflow-hidden">
            <div
              className={`h-full ${usedPct >= 80 ? 'bg-amber-500' : 'bg-brand-blue'}`}
              style={{ width: `${usedPct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
